'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

import dynamic from 'next/dynamic';
import Image from 'next/image';

import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import { AccountMenu } from '@/components/layout/AccountMenu';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Link, usePathname } from '@/i18n/routing';
import { megaMenuSections, type MegaMenuSectionKey } from '@/lib/services/mega-menu';
import { cn } from '@/lib/utils';

const MobileDrawer = dynamic(
  () => import('@/components/layout/MobileDrawer').then(mod => ({ default: mod.MobileDrawer })),
  { ssr: false },
);

const primaryNavHrefs = [
  { href: '/pricing', navKey: 'pricing' as const },
  { href: '/about', navKey: 'about' as const },
  { href: '/contact', navKey: 'contact' as const },
];

/**
 * The mega menu catalogue lives in `lib/services/mega-menu.ts` and mirrors the
 * service registry, so the per-category service counts rendered in the menu
 * footer always match the real catalogue.
 */
export function Header() {
  const locale = useLocale();
  const isGerman = locale === 'de';
  const tNav = useTranslations('common.nav');
  const tHeader = useTranslations('common.header');
  const tServiceLabels = useTranslations('common.footer.serviceLabels');

  const getServiceSectionHref = (
    labelKey: MegaMenuSectionKey,
    defaultHref: string,
  ) => {
    if (!isGerman) {
      return defaultHref;
    }

    if (labelKey === 'megaMenuBuildings') {
      return '/services/aussenbereich';
    }

    return defaultHref;
  };

  const servicesMenu = megaMenuSections.map(section => ({
    ...section,
    href: getServiceSectionHref(section.labelKey, section.href),
    label: tHeader(section.labelKey),
    items: section.items.map(item => ({
      ...item,
      label: tServiceLabels(item.serviceLabelKey),
    })),
  }));

  const primaryNavLinks = primaryNavHrefs.map(({ href, navKey }) => ({
    href,
    label: tNav(navKey),
  }));

  const pathname = usePathname();
  const headerRef = useRef<HTMLDivElement>(null);
  const servicesTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [servicesPopoverContainer, setServicesPopoverContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [servicesAlignOffset, setServicesAlignOffset] = useState(0);
  const setServicesPopoverRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setServicesPopoverContainer(node);
    }
  }, []);

  const updateServicesAlignOffset = useCallback(() => {
    // Use requestAnimationFrame to batch layout reads and avoid forced reflow
    requestAnimationFrame(() => {
      const trigger = servicesTriggerRef.current;

      if (!trigger || typeof window === 'undefined') {
        setServicesAlignOffset(0);
        return;
      }

      const rect = trigger.getBoundingClientRect();
      if (!rect.width) {
        setServicesAlignOffset(0);
        return;
      }

      const viewportCenter = window.innerWidth / 2;
      const triggerCenter = rect.left + rect.width / 2;
      setServicesAlignOffset(Math.round(viewportCenter - triggerCenter));
    });
  }, []);

  // Check if we're on the main page (normalize pathname to handle edge cases)
  const normalizedPathname = pathname?.split('?')[0]?.split('#')[0] || '';

  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileDrawerMounted, setIsMobileDrawerMounted] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpenRef = useRef(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<null | 'services'>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const pricingLink = primaryNavLinks.find(link => link.href === '/pricing');
  const secondaryNavLinks = primaryNavLinks.filter(link => link.href !== '/pricing');

  // Scroll detection for navbar hide/show
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollY = useRef(0);

  // Close drawer on route change
  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      setIsMobileDrawerMounted(true);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (wasMenuOpenRef.current && !isMenuOpen) {
      menuButtonRef.current?.focus();
    }
    wasMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  // Scroll lock is handled by MobileDrawer component

  useEffect(() => {
    const handleResize = () => {
      updateServicesAlignOffset();
    };
    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateServicesAlignOffset]);

  // Close popover when menu or language selector opens
  const prevIsMenuOpenRef = useRef(isMenuOpen);
  const prevIsLanguageOpenRef = useRef(isLanguageOpen);
  useEffect(() => {
    if (
      (isMenuOpen && !prevIsMenuOpenRef.current) ||
      (isLanguageOpen && !prevIsLanguageOpenRef.current)
    ) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setOpenPopoverId(null);
      }, 0);
    }
    prevIsMenuOpenRef.current = isMenuOpen;
    prevIsLanguageOpenRef.current = isLanguageOpen;
  }, [isMenuOpen, isLanguageOpen]);

  // Close popover on pathname change
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPathnameRef.current) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setOpenPopoverId(null);
      }, 0);
      prevPathnameRef.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    if (!openPopoverId) {
      return;
    }

    const closePopovers = () => setOpenPopoverId(null);
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const header = headerRef.current;

      if (!header || !target || !header.contains(target)) {
        closePopovers();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePopovers();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', closePopovers, { passive: true });
    window.addEventListener('wheel', closePopovers, { passive: true });
    window.addEventListener('touchmove', closePopovers, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', closePopovers);
      window.removeEventListener('wheel', closePopovers);
      window.removeEventListener('touchmove', closePopovers);
    };
  }, [openPopoverId]);

  // Handle scroll detection for navbar hide/show
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const atTop = currentScrollY < 10;

      // Don't hide if at top of page or menu is open
      if (atTop || isMenuOpen) {
        setIsScrollingDown(false);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Determine scroll direction
      if (currentScrollY > lastScrollY.current) {
        // Scrolling down
        setIsScrollingDown(true);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up
        setIsScrollingDown(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]);

  return (
    <header
      ref={headerRef}
      style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
      className={cn(
        'fixed inset-x-0 top-0 z-[200] transition-transform duration-300 ease-in-out [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        // Hide header when scrolling down (but not when menu is open)
        isScrollingDown && !isMenuOpen && '-translate-y-full',
        isMenuOpen ? 'border-b-0' : 'border-b border-oxford-blue/10',
        'bg-white',
        // Header stays above overlay (z-[200] > z-[140]/z-[150])
      )}
    >
      {/* Container wrapper for proper centering on large screens */}
      <div
        ref={setServicesPopoverRef}
        className='relative mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8'
      >
        {/* Main header bar: single row, three columns */}
        <div className='grid grid-cols-[minmax(0,auto)_1fr_minmax(0,auto)] grid-rows-1 items-center h-16 gap-3'>
          {/* Logo — column 1, must not grow */}
          <Link
            href='/'
            className='col-start-1 row-start-1 flex w-fit min-w-0 items-center gap-2 flex-shrink-0'
            aria-label='Kleaners - Go to homepage'
          >
            <div className='w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center flex-shrink-0'>
              <Image
                src='/images/Logos/logo-1.svg'
                alt='Kleaners Logo'
                width={48}
                height={48}
                className='w-10 h-10 sm:w-11 sm:h-11 brightness-0'
                unoptimized
                style={{
                  backgroundColor: 'transparent',
                  objectFit: 'contain',
                }}
              />
            </div>
            <span className='text-lg sm:text-xl font-semibold leading-none tracking-wide text-oxford-blue'>
              Kleaners
            </span>
          </Link>

          <nav className='col-start-2 row-start-1 hidden lg:flex items-center justify-center gap-8 h-10 sm:h-11 min-w-0'>
            {pricingLink && (
              <Link
                href={pricingLink.href}
                aria-current={normalizedPathname === pricingLink.href ? 'page' : undefined}
                className={cn(
                  'flex items-center h-full text-base font-medium tracking-[0.02em] leading-none whitespace-nowrap transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                  'text-oxford-blue/80 hover:text-oxford-blue',
                  normalizedPathname === pricingLink.href &&
                    'text-oxford-blue border-b-2 border-orange-peel',
                )}
              >
                {pricingLink.label}
              </Link>
            )}
            <Popover
              open={openPopoverId === 'services'}
              onOpenChange={open => {
                if (open) {
                  setOpenPopoverId('services');
                  setIsMenuOpen(false);
                  setIsLanguageOpen(false);
                  updateServicesAlignOffset();
                } else {
                  setOpenPopoverId(null);
                }
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type='button'
                  aria-haspopup='menu'
                  ref={servicesTriggerRef}
                  className={cn(
                    'flex items-center h-full gap-1.5 text-base font-medium tracking-[0.02em] leading-none whitespace-nowrap transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                    'text-oxford-blue/80 hover:text-oxford-blue',
                    openPopoverId === 'services' && 'text-oxford-blue',
                  )}
                >
                  {tNav('services')}
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-200',
                      openPopoverId === 'services' && 'rotate-180',
                    )}
                    aria-hidden='true'
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align='center'
                alignOffset={servicesAlignOffset}
                side='bottom'
                sideOffset={0}
                collisionPadding={0}
                container={servicesPopoverContainer}
                className='w-[min(60rem,calc(100vw-1.5rem))] max-w-[min(60rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-oxford-blue/12 bg-white text-oxford-blue shadow-[0_24px_64px_rgba(0,27,46,0.22)]'
              >
                <div className='flex flex-col'>
                  <div
                    role='group'
                    aria-label={tNav('services')}
                    className='flex flex-wrap items-center gap-2 border-b border-oxford-blue/8 px-4 pb-4 pt-4'
                  >
                    {servicesMenu.map((section, index) => {
                      const isActive = activeSectionIndex === index;
                      return (
                        <button
                          key={section.labelKey}
                          type='button'
                          aria-pressed={isActive}
                          onClick={() => setActiveSectionIndex(index)}
                          className={cn(
                            'rounded-md px-4 py-2 text-base font-medium transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                            isActive
                              ? 'bg-orange-peel text-white hover:bg-orange-peel/90'
                              : 'border border-oxford-blue/15 text-oxford-blue hover:text-oxford-blue',
                          )}
                        >
                          {section.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className='grid grid-cols-1 gap-x-8 py-2 sm:grid-cols-2'>
                    {servicesMenu[activeSectionIndex].items.map(item => (
                      <Link
                        key={item.serviceLabelKey}
                        href={item.href}
                        className='block rounded-md px-4 py-2.5 text-base font-normal text-oxford-blue/80 transition-colors hover:bg-oxford-blue/[0.05] hover:text-oxford-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-inset'
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                  <div className='flex flex-wrap items-center justify-between gap-2 border-t border-oxford-blue/8 px-4 py-3'>
                    <span className='text-sm font-normal text-oxford-blue/60'>
                      {servicesMenu[activeSectionIndex].items.length}{' '}
                      {tHeader('megaMenuCategoryCount')}
                    </span>
                    <Link
                      href='/services'
                      className='inline-flex items-center gap-1 text-sm font-medium text-oxford-blue transition-colors hover:text-orange-peel-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white'
                    >
                      {tHeader('megaMenuViewAll')}
                      <ChevronRight className='h-3 w-3' aria-hidden='true' />
                    </Link>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {secondaryNavLinks.map(link => {
              const isActive = normalizedPathname === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center h-full text-base font-medium tracking-[0.02em] leading-none whitespace-nowrap transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                    'text-oxford-blue/80 hover:text-oxford-blue',
                    isActive && 'text-oxford-blue border-b-2 border-orange-peel',
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions — column 3, same height as nav */}
          <div className='col-start-3 row-start-1 flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0 h-10 sm:h-11 min-w-0'>
            <div className='flex items-center'>
              <LanguageSwitcher
                variant='header'
                open={isLanguageOpen}
                onOpenChange={nextOpen => {
                  setIsLanguageOpen(nextOpen);
                  if (nextOpen) {
                    setOpenPopoverId(null);
                    setIsMenuOpen(false);
                  }
                }}
                onClose={() => {
                  setIsLanguageOpen(false);
                  setOpenPopoverId(null);
                }}
              />
            </div>

            <div className='hidden lg:flex items-center gap-3'>
              <AccountMenu variant='header' />
              <Button
                asChild
                size='sm'
                className={cn(
                  'rounded-lg px-5 !h-9 !text-sm font-semibold tracking-wide !leading-none whitespace-nowrap !mb-0',
                  'bg-orange-peel text-white shadow-sm hover:bg-orange-peel/90 focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                )}
              >
                <Link href='/book'>{tNav('booking')}</Link>
              </Button>
            </div>

            {/* Hamburger Menu Button */}
            <button
              type='button'
              ref={menuButtonRef}
              onPointerEnter={() => setIsMobileDrawerMounted(true)}
              onFocus={() => setIsMobileDrawerMounted(true)}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsLanguageOpen(false);
                setIsMenuOpen(prev => !prev);
              }}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls='mobile-menu'
              className={cn(
                'lg:hidden',
                'flex items-center justify-center',
                'h-10 w-10',
                'rounded-lg',
                'border-0 bg-transparent text-oxford-blue',
                'hover:bg-oxford-blue/[0.06]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                'transition-colors duration-200 touch-manipulation',
              )}
            >
              {isMenuOpen ? (
                <X className='h-5 w-5' aria-hidden='true' />
              ) : (
                <Menu className='h-5 w-5' aria-hidden='true' />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileDrawerMounted ? (
        <MobileDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      ) : null}
    </header>
  );
}
